<template>
    <Head :title="t('perspective.pageTitle')" />
    <!-- Covers the survey while the wheel lands, so the last thing the user sees here is their own
         archetype rather than the questions they just answered. -->
    <!-- The region wraps the transition and is mounted from the start: the overlay arrives with no
         text, and the archetype line is added once the wheel lands, which a screen reader announces
         as an addition to a region it already knows. The overlay is absolutely positioned, so the
         wrapper takes no space. -->
    <div role="status" aria-live="polite">
        <Transition name="wheel-fade">
            <div v-if="resultLetters !== null" class="wheel-overlay">
                <PerspectiveWheelLoader :land="resultLetters" @landed="onWheelLanded" />
                <span v-if="announcedType !== ''" class="sr-only">{{ t("perspective.questions.typeAnnouncement", { type: announcedType }) }}</span>
            </div>
        </Transition>
    </div>
    <component :is="surveyComponent" ref="survey" :questions="questions" :saved-responses="saved_responses" :session-id="session_id" sync-url @submitted="onSubmitted">
        <template #escape>
            <BaseButton type="button" theme="tertiary" size="sm" class="inline-flex items-center gap-2" @click="showCancelConfirm = true">
                <i class="bi bi-x-circle" aria-hidden="true"></i>
                {{ t("perspective.questions.cancelAssessment") }}
            </BaseButton>
        </template>
    </component>
    <ConfirmationDialog
        v-model:open="showCancelConfirm"
        :title="t('perspective.questions.cancelConfirm.title')"
        :text="t('perspective.questions.cancelConfirm.text')"
        :confirm-label="t('perspective.questions.cancelAssessment')"
        :cancel-label="t('perspective.questions.cancelConfirm.keepGoing')"
        @confirm="executeCancel" />
    <PerspectiveDemoTools :session-id="session_id" @filled="handleFilled" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import { type ShareBackTarget, withShareBackTarget } from "~vue/CollaborationProfile/composables/useProfileShareBack";
import BaseButton from "~vue/components/BaseButton.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import PerspectiveWheelLoader from "~vue/components/PerspectiveWheelLoader.vue";
import { PERSPECTIVE_TYPES, type Question } from "~vue/pages/perspectiveTypes";
import PerspectiveDemoTools from "~vue/Perspective/PerspectiveDemoTools.vue";
import type PerspectiveSurvey from "~vue/Perspective/PerspectiveSurvey.vue";
import { usePerspectiveSurveyComponent } from "~vue/Perspective/usePerspectiveSurveyComponent";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { analyticsSourceFromQuery, logUserInteraction } from "~vue/utils/logUtils";
import { onUnmounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

export interface Props {
    questions: Question[];
    saved_responses: Record<string, number>;
    session_id: string;
    share_back?: boolean;
    share_back_target?: ShareBackTarget | null;
}

const props = defineProps<Props>();
const { t } = useI18n();

// Deep links into the assessment (e.g. the Teams feature nudge via /perspective/start/)
// carry ?source=<slug> through the redirect; keep it on the completion event so Amplitude
// can attribute the finished assessment to the surface that drove it.
const source = analyticsSourceFromQuery("perspective_assessment");

const { surveyComponent } = usePerspectiveSurveyComponent();
const survey = useTemplateRef<InstanceType<typeof PerspectiveSurvey>>("survey");
const showCancelConfirm = ref(false);

// ── Wheel animation ──
/** Non-null once there is a type to land on, which is also what puts the wheel on screen. */
const resultLetters = ref<string | null>(null);
const announcedType = ref("");
/* Long enough for the landed type to be read out, and to be looked at, before the page changes. */
const ANNOUNCE_MS = 1200;
let leaveTimer: ReturnType<typeof setTimeout> | null = null;

async function onSubmitted(payload: unknown) {
    await logUserInteraction(AmplitudeEventNames.PERSPECTIVE_ASSESSMENT_COMPLETED, { type: "perspective", source });
    const letters = (payload as { letters?: string } | null)?.letters ?? null;
    // Share-back goes straight to collaboration profile creation, without an archetype reveal.
    if (props.share_back || letters === null) {
        onWheelDone();
        return;
    }
    resultLetters.value = letters;
}

function onWheelLanded() {
    announcedType.value = PERSPECTIVE_TYPES[resultLetters.value ?? ""]?.title ?? "";
    leaveTimer = setTimeout(onWheelDone, ANNOUNCE_MS);
}

function onWheelDone() {
    router.visit(props.share_back ? withShareBackTarget("/perspective/?source=profile_share_back", props.share_back_target?.id) : "/perspective/");
}

onUnmounted(() => {
    if (leaveTimer !== null) {
        clearTimeout(leaveTimer);
    }
});

const { execute: executeCancel, onFetchResponse: onCancelResponse } = useFetch(`/v1/perspectives/sessions/${props.session_id}`, { immediate: false }).delete();

onCancelResponse(() => {
    router.visit("/perspective/");
});

function handleFilled(filled: Record<string, number>) {
    survey.value?.adoptResponses(filled);
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* The sidebar layout is the positioned ancestor, so this covers the survey and nothing else. */
    .wheel-overlay {
        @apply absolute inset-0 z-20 flex flex-col items-center justify-center bg-surface;
    }
}

.wheel-fade-enter-active {
    @apply transition-opacity duration-300;
}

.wheel-fade-enter-from {
    @apply opacity-0;
}
</style>
